import { useState } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { IconChevronDownSmall } from '@components/icons/chevron-down-small';
import { IconChevronRightSmall } from '@components/icons/chevron-right-small';
import { IconGear } from '@components/icons/gear';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';

import { PageContent } from 'components/Page';
import { useTriggerConfigSidebar } from 'integrations/journeys/job-triggers/trigger-config-sidebar';

import { useJobJourneys, useSetJobJourneyEnabled } from './hooks';

import { JourneyListSection } from '../components/journey-list-section';
import { JOURNEYS_LIST_CAP } from '../components/journey-search';
import { journeyListStrings } from '../components/list-strings';
import { RawButton } from '../components/raw-button';
import { SectionBox } from '../components/section-box';
import { JourneyListRow } from '../components/types';
import { useIsAppsAdmin } from '../hooks';

type JourneysTabProps = {
  jobId: number;
};

const INFO_CONTENT_ID = 'job-journeys-info-content';

/**
 * Per-job Journeys tab.
 *
 * Lists globally-enabled journeys with a per-job availability toggle,
 * grouped by workspace with search and available/hidden filters.
 * A "Configure" button opens the per-job trigger config sidebar.
 * The company-wide allow-list link is shown only for admin/superadmin users.
 */
export const JourneysTab = ({ jobId }: JourneysTabProps) => {
  const { t } = useScopedTranslate(locale);
  const [search, setSearch] = useState('');
  const journeysQuery = useJobJourneys(jobId, search);
  const setEnabled = useSetJobJourneyEnabled(jobId);
  const openConfigSidebar = useTriggerConfigSidebar();
  const isAdmin = useIsAppsAdmin();

  const [isInfoCollapsed, setIsInfoCollapsed] = useState(false);

  const handleToggle = (journey: JourneyListRow, isEnabled: boolean) => {
    setEnabled.mutate({
      journeyId: journey.journeyId,
      // Toggle on = null (revert to inherit = enabled); toggle off = false
      isEnabled: isEnabled ? null : false,
      journeyTitle: journey.title,
    });
  };

  const handleConfigure = (journey: JourneyListRow) => {
    openConfigSidebar({
      jobId,
      journeyId: journey.journeyId,
      journeyTitle: journey.title,
    });
  };

  return (
    /**
     * The job page shell is overflow-y: hidden, so each job route owns its own
     * vertical scrolling — PageContent measures its offset from the top of the
     * viewport and sets an explicit height, the same way the Applicants tab
     * does. Without it this tab's content was clipped with no way to reach the
     * journeys below the fold. The company Manage tab never had the problem:
     * its route already wraps integration pages in a PageContent.
     */
    <PageContent noPadding data-testid="job-journeys-scroll">
      <Container data-testid="job-journeys-tab">
        <Stack space={5}>
          <SectionBox>
            <Stack space={5}>
              <SectionHeader
                type="button"
                onClick={() => setIsInfoCollapsed((previous) => !previous)}
                aria-expanded={!isInfoCollapsed}
                aria-controls={INFO_CONTENT_ID}
              >
                <Stack space={3} orientation="horizontal" align="center">
                  <IconJourneyItem size={24} color="indigo200" />
                  <Text font="bold16" text={t('infoTitle')} />
                </Stack>
                {isInfoCollapsed ? (
                  <IconChevronRightSmall size={16} color="textHelper" />
                ) : (
                  <IconChevronDownSmall size={16} color="textHelper" />
                )}
              </SectionHeader>
              <div id={INFO_CONTENT_ID} hidden={isInfoCollapsed}>
                <Stack space={5}>
                  <Text font="regular14" color="textSecondary" text={t('infoDescription')} />
                  {isAdmin && (
                    <ManageLink>
                      <Button
                        variant="secondary"
                        iconLeft={<IconGear />}
                        text={t('manageAllowList')}
                        href="/settings/apps/journeys"
                        dataTestid="manage-journeys-settings-link"
                      />
                    </ManageLink>
                  )}
                </Stack>
              </div>
            </Stack>
          </SectionBox>

          <SectionBox>
            <Stack space={5}>
              <Text font="bold16" text={t('availableTitle')} />
              <Text font="regular14" color="textSecondary" text={t('availableDescription')} />

              <JourneyListSection
                journeys={journeysQuery.data?.results}
                isLoading={journeysQuery.isLoading}
                isError={journeysQuery.isError}
                isTruncated={journeysQuery.data?.isTruncated}
                searchValue={search}
                onSearchChange={setSearch}
                onToggle={handleToggle}
                onConfigure={handleConfigure}
                onRetry={() => journeysQuery.refetch()}
                mutatingJourneyId={setEnabled.isLoading ? setEnabled.variables?.journeyId : null}
                journeyRowCy="job-journey-row"
                toggleCy="journey-availability-toggle"
                searchTestid="job-journeys-search"
                loadingCy="job-journeys-loading"
                errorCy="job-journeys-error"
                labels={{
                  all: t('all'),
                  enabled: t('available'),
                  disabled: t('hidden'),
                  searchPlaceholder: t('searchPlaceholder'),
                  noJourneys: isAdmin ? t('noJourneysAdmin') : t('noJourneysNonAdmin'),
                  noResults: t('noResults'),
                  errorDescription: t('loadError'),
                  retry: t('retry'),
                  configure: t('configure'),
                  toggleEnabledLabel: t('available'),
                  toggleDisabledLabel: t('hidden'),
                  showingFirstResults: t('showingFirstResults', {
                    count: String(JOURNEYS_LIST_CAP),
                  }),
                }}
              />
            </Stack>
          </SectionBox>
        </Stack>
      </Container>
    </PageContent>
  );
};

const Container = styled.div({
  padding: theme.space[4],
  // Without width, this sizes to its content inside the column-flex page shell, so a
  // long journey title pushes it to maxWidth and scrolls the row's toggle out of view.
  width: '100%',
  maxWidth: pxToRem(960),
  margin: '0 auto',
});

const SectionHeader = styled(RawButton)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  background: 'none',
  padding: 0,
  transition: transitionStandard('opacity'),
  '&:hover': {
    opacity: 0.7,
  },
});

const ManageLink = styled.div({
  display: 'flex',
});

const locale = {
  en: {
    ...journeyListStrings.en,
    infoTitle: 'Journeys for this job',
    infoDescription:
      "Pick which journeys can be used for this job. The list starts from your company-wide allow-list — narrow it further so your team sees only what's relevant.",
    manageAllowList: 'Manage the company-wide allow-list',
    availableTitle: 'Available journeys',
    availableDescription:
      'Choose which journeys are available for this job and configure which fields need to be filled in when sending applicants.',
    all: 'All',
    searchPlaceholder: 'Search...',
    configure: 'Configure',
    available: 'Available',
    hidden: 'Hidden',
    noJourneysAdmin: 'No journeys have been enabled globally. Enable journeys in company settings.',
    noJourneysNonAdmin:
      'No journeys are available for this job. Ask an administrator to enable journeys for your company.',
    retry: 'Retry',
  },
  is: {
    ...journeyListStrings.is,
    infoTitle: 'Journey-ferli fyrir þetta starf',
    infoDescription:
      'Veldu hvaða Journey-ferli má nota fyrir þetta starf. Listinn sýnir ferlin sem fyrirtækið hefur heimilað. Þú getur takmarkað úrvalið frekar svo teymið sjái aðeins þau sem eiga við.',
    manageAllowList: 'Velja hvaða ferli eru heimiluð hjá fyrirtækinu',
    availableTitle: 'Journey-ferli í boði',
    availableDescription:
      'Veldu hvaða Journey-ferli eru í boði fyrir þetta starf og hvaða reiti þarf að fylla út þegar umsækjendur eru sendir í þau.',
    all: 'Öll',
    searchPlaceholder: 'Leita…',
    configure: 'Stilla',
    available: 'Í boði',
    hidden: 'Falið',
    noJourneysAdmin:
      'Engin Journey-ferli hafa verið virkjuð fyrir fyrirtækið. Virkjaðu þau í stillingum fyrirtækisins.',
    noJourneysNonAdmin:
      'Engin Journey-ferli eru í boði fyrir þetta starf. Biddu kerfisstjóra um að virkja þau fyrir fyrirtækið.',
    retry: 'Reyna aftur',
  },
};
